import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import { type UserActivityLogFiltersType } from "#src/batteries-included-components/FilterAreas/UsersFilterAreas/UsersFilterAreas";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import {
  useExportActivityLogs,
  useListActivityLogs,
} from "#src/components/hooks/adapters/useActivityLogs";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useHasPermission } from "#src/contexts/AuthenticatedContext.helpers";
import { useStickyState } from "#src/hooks/useStickyState";
import { useNavigate } from "#src/Routers/hooks";
import { UserGroupDetailsRoutePath } from "#src/routes/settings/users/groups/[groupId]/details";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  type HeaderType,
  Icon,
  Pill,
  StorageKeys,
} from "@validereinc/common-components";
import {
  ActivityAction,
  ActivityAdapter,
  ActivityExportResource,
  ActivityResource,
  ActivitySchema,
  type ActivityType,
  Resources,
  SortDirection,
  UserGroupsAdapter,
  type UserGroupType,
  UserTypeEnum,
  type UserWithGroupsType,
} from "@validereinc/domain";
import { isEmpty } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";

const activityModelKeys = ActivitySchema.keyof().Enum;

type UserGroupActivityLogBeforeAfter = { user_id: string };

export const UserGroupsActivityLogTablePanel = ({
  viewConfigStorageKey,
  tableStateStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey" | "tableStateStorageKey">) => {
  const navigate = useNavigate();
  const [viewFilters] = useStickyState<UserActivityLogFiltersType>(
    {} as UserActivityLogFiltersType,
    viewConfigStorageKey
  );

  const { resource_id, logged_by, logged_at } = viewFilters;

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "timestamp",
      sortDirection: SortDirection.DESCENDING,
      pageSize: 25,
    },
    viewFilters,
    tableStateStorageKey
  );
  const [selectedActivities, setSelectedActivities] = useState<
    Record<string, ActivityType>
  >({});
  const [canExportPermission] = useHasPermission({
    $and: ["reports:read", "reports:write"],
  });

  const viewFiltersToApply = {
    ...(resource_id && { resource_id }),
    ...(logged_by && ({ author_id: logged_by } as any)),
    ...(logged_at && ({ timestamp: logged_at } as any)),
  };

  const activitiesQueryPayload = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...viewFiltersToApply,
      action: [ActivityAction.USER_ADD, ActivityAction.USER_REMOVE],
    },
    meta: { resourceType: ActivityResource.USER_GROUP },
  } satisfies Parameters<typeof ActivityAdapter.getList>[0];

  const { data: activitiesData, isLoading: areActivityLogsLoading } =
    useListActivityLogs(activitiesQueryPayload);

  const activities = activitiesData?.data;

  // We cannot use the BE to filter on user IDs nested in before/after
  const filteredActivities = useMemo(() => {
    if (!viewFilters.user_id || !activities) {
      return activities ?? [];
    }

    return activities.filter((activity) => {
      const beforeUserId = (activity.before as UserGroupActivityLogBeforeAfter)
        ?.user_id;
      const afterUserId = (activity.after as UserGroupActivityLogBeforeAfter)
        ?.user_id;
      return (
        viewFilters.user_id?.includes(beforeUserId) ||
        viewFilters.user_id?.includes(afterUserId)
      );
    });
  }, [activities, viewFilters.user_id]);

  const processActivityUserGroups = useMemo(() => {
    const uniqueUserIds = new Set<string>();
    const uniqueUserGroupIds = new Set<string>();

    activities?.forEach((activity: ActivityType) => {
      uniqueUserIds.add(activity.author_id);
      const beforeUserId = (activity.before as UserGroupActivityLogBeforeAfter)
        ?.user_id;
      const afterUserId = (activity.after as UserGroupActivityLogBeforeAfter)
        ?.user_id;

      if (beforeUserId) uniqueUserIds.add(beforeUserId);
      if (afterUserId) uniqueUserIds.add(afterUserId);

      uniqueUserGroupIds.add(activity.resource_id);
    });

    const uniqueUserIdsArray = Array.from(uniqueUserIds);
    const uniqueUserGroupIdsArray = Array.from(uniqueUserGroupIds);

    return {
      uniqueUserIdsArray,
      uniqueUserGroupIdsArray,
    };
  }, [activities]);

  const { uniqueUserIdsArray, uniqueUserGroupIdsArray } =
    processActivityUserGroups;

  const { data: usersQueryData, isLoading: isLoadingUsers } = useListUsers(
    {
      filters: {
        id: uniqueUserIdsArray,
        type: [UserTypeEnum.human, UserTypeEnum.machine],
      },
    },
    {
      enabled: Boolean(uniqueUserIdsArray.length),
    }
  );
  const users = usersQueryData?.data ?? [];
  const userIdsToUserDetailsMap = useMemo(() => {
    return Object.fromEntries(users.map((user) => [user.id, user])) as Record<
      string,
      UserWithGroupsType
    >;
  }, [users]);

  const { data: userGroupsQueryData, isLoading: isLoadingUserGroups } =
    useQuery({
      queryKey: [
        Resources.USER_GROUP,
        { filters: { id: uniqueUserGroupIdsArray } },
      ],
      queryFn: () =>
        UserGroupsAdapter.getList({ filters: { id: uniqueUserGroupIdsArray } }),
      enabled: Boolean(uniqueUserGroupIdsArray.length),
    });
  const userGroups = userGroupsQueryData?.data ?? [];
  const userGroupIdsToUserGroupDetailsMap = useMemo(() => {
    return Object.fromEntries(
      userGroups.map((userGroup) => [userGroup.id, userGroup])
    ) as Record<string, UserGroupType>;
  }, [userGroups]);

  const exportActivityLogPayload = {
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...(!isEmpty(selectedActivities)
        ? { id: Object.keys(selectedActivities) }
        : viewFilters.user_id
          ? {
              id: filteredActivities.map(
                (filteredActvity) => filteredActvity.id
              ),
            }
          : {}),
      ...viewFiltersToApply,
    },
    meta: { resourceType: ActivityExportResource.USER_GROUP, period: "" },
  } satisfies Parameters<typeof ActivityAdapter.exportList>[0];

  const { mutate: handleExport, isLoading: isExporting } =
    useExportActivityLogs(exportActivityLogPayload);

  const isLoading =
    areActivityLogsLoading ||
    (Boolean(uniqueUserIdsArray.length) && isLoadingUsers) ||
    (Boolean(uniqueUserGroupIdsArray.length) && isLoadingUserGroups);

  const headers = useMemo<Array<HeaderType<ActivityType>>>(
    () => [
      {
        key: "user",
        label: "User",
        renderComponent: ({ item }) => {
          const userId =
            (item.before as UserGroupActivityLogBeforeAfter)?.user_id ??
            (item.after as UserGroupActivityLogBeforeAfter)?.user_id ??
            "";
          const userName = userIdsToUserDetailsMap[userId]?.name;
          return userId && userName ? (
            <UserDataDisplay
              id={userId}
              name={userName}
              isLoading={isLoading}
            />
          ) : (
            "-"
          );
        },
      },
      {
        key: activityModelKeys.action,
        label: "Type",
        renderComponent: ({ item }) => {
          return item.action === ActivityAction.USER_ADD ? (
            <Pill
              variant="success"
              isLoading={isLoading}
            >
              Added to Group
            </Pill>
          ) : item.action === ActivityAction.USER_REMOVE ? (
            <Pill
              variant="error"
              isLoading={isLoading}
            >
              Removed from Group
            </Pill>
          ) : null;
        },
      },
      {
        key: activityModelKeys.resource_id,
        label: "User Group",
        renderComponent: ({ item }) => {
          const userGroupId = item.resource_id;
          const userGroupName =
            userGroupIdsToUserGroupDetailsMap[userGroupId]?.name;
          return userGroupId && userGroupName ? (
            <Pill
              variant="primary"
              isBordered
              isLoading={isLoading}
            >
              <div
                onClick={() => {
                  navigate({
                    pathname: UserGroupDetailsRoutePath.toLink({
                      pathParams: { groupId: userGroupId },
                    }),
                  });
                }}
              >
                {userGroupName}
              </div>
            </Pill>
          ) : (
            "-"
          );
        },
      },
      {
        key: activityModelKeys.author_id,
        label: "Logged By",
        renderComponent: ({ item }) => {
          const authorId = item.author_id;
          const authorName = userIdsToUserDetailsMap[authorId]?.name ?? "-";
          return authorId && authorName ? (
            <UserDataDisplay
              id={authorId}
              name={authorName}
              isLoading={isLoading}
            />
          ) : (
            "-"
          );
        },
      },
      {
        key: activityModelKeys.timestamp,
        label: "Logged At",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item[activityModelKeys.timestamp]}
            withTime
            isLoading={isLoading}
          />
        ),
      },
    ],
    [userIdsToUserDetailsMap, userGroupIdsToUserGroupDetailsMap]
  );

  const actionRow = [
    <PermissionAwareExportButton
      key="export"
      onClick={handleExport}
      isExporting={isExporting}
    />,
  ];

  return (
    <DataTablePanel
      panelProps={{
        title: "User Group Change Log",
        actionRow,
      }}
      dataTableProps={{
        headers,
        items: filteredActivities ?? [],
        isLoading,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: activitiesData?.total_entries,
          pageSizeText: "logs per page",
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        selected: selectedActivities,
        onSelectionChange: setSelectedActivities,
        getItemId: (item) => item.id,
        getItemBulkActions: ({ buttonProps }) => [
          canExportPermission ? (
            <Button
              {...buttonProps}
              key="export"
              variant="outline"
              slotLeft={<Icon variant="arrow-square-out" />}
              isLoading={isExporting}
              onClick={() => handleExport()}
            >
              Export
            </Button>
          ) : null,
        ],
      }}
    />
  );
};
